import { useEffect, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ShoppingCart, Minus, Plus, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { formatPriceNok } from "@/lib/shop";
import {
  FLY_TO_CART_EVENT,
  runFlyToCart,
  type FlyToCartDetail,
} from "@/lib/fly-to-cart";

export const CartDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const items = useCartStore((s) => s.items);
  const isCheckingOut = useCartStore((s) => s.isCheckingOut);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const removeItem = useCartStore((s) => s.removeItem);
  const checkout = useCartStore((s) => s.checkout);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalOre = items.reduce((sum, i) => sum + i.priceOre * i.quantity, 0);

  useEffect(() => {
    const onFly = (event: Event) => {
      const detail = (event as CustomEvent<FlyToCartDetail>).detail;
      const target = triggerRef.current;
      if (!target || !detail) return;
      runFlyToCart(target, detail);
    };
    window.addEventListener(FLY_TO_CART_EVENT, onFly);
    return () => window.removeEventListener(FLY_TO_CART_EVENT, onFly);
  }, []);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button
          ref={triggerRef}
          className="relative rounded-full p-2 transition-colors hover:bg-secondary"
          aria-label="Handlekurv"
        >
          <ShoppingCart className="h-5 w-5" />
          {totalItems > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
              {totalItems}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="flex h-full w-full flex-col sm:max-w-lg">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="font-heading">Handlekurv</SheetTitle>
          <SheetDescription className="font-body">
            {totalItems === 0
              ? "Handlekurven er tom"
              : `${totalItems} vare${totalItems !== 1 ? "r" : ""} i kurven`}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 flex-col pt-6 min-h-0">
          {items.length === 0 ? (
            <div className="flex flex-1 items-center justify-center">
              <div className="text-center">
                <ShoppingCart className="mx-auto h-12 w-12 text-muted-foreground" />
                <p className="mt-4 text-muted-foreground font-body">
                  Handlekurven din er tom
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                <div className="space-y-4">
                  {items.map((item) => (
                    <div
                      key={item.variantId}
                      className="flex gap-4 rounded-xl p-3 bg-secondary/30"
                    >
                      <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-secondary">
                        {item.imageUrl && (
                          <img
                            src={item.imageUrl}
                            alt={item.productTitle}
                            className="h-full w-full object-cover"
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="truncate font-heading text-sm font-bold">
                          {item.productTitle}
                        </h4>
                        {item.variantTitle && item.variantTitle !== "Default" && (
                          <p className="text-xs text-muted-foreground font-body">
                            {item.variantTitle}
                          </p>
                        )}
                        <p className="mt-1 font-body font-semibold text-sm">
                          {formatPriceNok(item.priceOre)}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <button
                          onClick={() => removeItem(item.variantId)}
                          className="rounded-full p-1 text-muted-foreground hover:text-destructive transition-colors"
                          aria-label="Fjern fra handlekurv"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() =>
                              updateQuantity(item.variantId, item.quantity - 1)
                            }
                            className="flex h-6 w-6 items-center justify-center rounded-full border border-border text-xs hover:bg-secondary"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-6 text-center text-xs font-body">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() =>
                              updateQuantity(item.variantId, item.quantity + 1)
                            }
                            className="flex h-6 w-6 items-center justify-center rounded-full border border-border text-xs hover:bg-secondary"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex-shrink-0 space-y-4 border-t pt-4 mt-4">
                <div className="flex items-center justify-between">
                  <span className="font-heading text-lg font-bold">Totalt</span>
                  <span className="font-body text-xl font-bold">
                    {formatPriceNok(totalOre)}
                  </span>
                </div>
                <button
                  onClick={checkout}
                  disabled={items.length === 0 || isCheckingOut}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 font-body font-semibold text-primary-foreground transition-all hover:opacity-90 disabled:opacity-50"
                >
                  {isCheckingOut ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <ExternalLink className="h-4 w-4" />
                      Gå til betaling
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
